/**
 * Client HTTP verso il gateway.
 *
 * L'autenticazione è **tutta lato server** (backend-for-frontend nel gateway,
 * `.claude/handoff/06`): il browser non vede né custodisce token. La sessione è
 * un cookie `HttpOnly; Secure; SameSite=Strict` che il browser allega da sé.
 *
 * Conseguenze, che sono anche le regole non negoziabili di questo file:
 *
 *  - ogni richiesta parte con `credentials: 'include'`, altrimenti il cookie di
 *    sessione non viene allegato e il gateway risponde 401;
 *  - ogni richiesta porta l'header `X-Requested-With: institor`. È la seconda
 *    difesa CSRF dopo `SameSite=Strict`: un modulo inviato da un altro sito non
 *    può impostare header personalizzati, quindi il gateway rifiuta con 403.
 *    Lo mandiamo anche sui `GET` — dove il gateway non lo pretende — perché una
 *    rotta che cambiasse metodo non debba ricordarsi di aggiungerlo;
 *  - nessun token viaggia nella query string (CLAUDE.md §10.4): per gli URL che
 *    il browser costruisce da sé (`<img src>`, `EventSource`, `<a download>`)
 *    si chiede al gateway un **ticket monouso** con una POST autenticata;
 *  - un 401 non viene mascherato né ritentato: significa sessione non più
 *    valida, e la UI deve riportare l'utente alla pagina di accesso.
 */

import { t } from '../i18n';

/** Nome dell'header che il gateway pretende sulle richieste con cookie. */
export const CSRF_HEADER = 'X-Requested-With';

/** Valore dell'header CSRF concordato con il gateway. */
export const CSRF_HEADER_VALUE = 'institor';

/** Errore restituito dal gateway o dai servizi. */
export class ApiError extends Error {
  /** Codice di stato HTTP della risposta. */
  readonly status: number;

  /**
   * Secondi da attendere prima di riprovare, letti dall'header `Retry-After`.
   * `null` se l'header manca o non è espresso in secondi interi.
   */
  readonly retryAfterSeconds: number | null;

  /**
   * Codice della regola di dominio violata, dal campo `code` del corpo
   * (ADR 0008, Appendice A: `relay_protected`, `upload_too_large`,
   * `github_token_invalid`...). `null` se il servizio non lo dichiara.
   */
  readonly code: string | null;

  /**
   * @param status Codice di stato HTTP.
   * @param message Messaggio destinato all'utente.
   * @param retryAfterSeconds Attesa suggerita dal server, se dichiarata.
   * @param code Codice della regola di dominio, se dichiarato.
   */
  constructor(
    status: number,
    message: string,
    retryAfterSeconds: number | null = null,
    code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
    this.code = code;
  }

  /** True se l'errore è dovuto a mancanza di permessi. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True se l'errore è dovuto a una sessione non più valida o a credenziali errate. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** True se il server ha imposto un freno ai tentativi. */
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/** Metodi HTTP usati dalla dashboard. */
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Opzioni della singola richiesta. */
export interface RequestOptions {
  /**
   * Se `true` (default) un 401 invoca `onUnauthenticated`, cioè riporta
   * l'applicazione allo stato anonimo.
   *
   * Va messo a `false` sulle rotte di autenticazione, dove il 401 è la risposta
   * attesa a una credenziale sbagliata: lì l'utente deve restare sulla pagina e
   * vedere il messaggio d'errore, non essere rimbalzato altrove.
   */
  notifyUnauthenticated?: boolean;
}

/** Opzioni di un caricamento binario. */
export interface UploadOptions {
  /** Valore dell'header `Content-Type`, es. `application/zip`. */
  contentType: string;
  /**
   * Invocata a ogni avanzamento dell'invio.
   *
   * @param loaded Byte già inviati.
   * @param total Byte totali da inviare.
   */
  onProgress?: (loaded: number, total: number) => void;
  /** Segnale per interrompere il caricamento. */
  signal?: AbortSignal;
  /** Header in più, per esempio `X-Filename` degli allegati (ADR 0016). */
  headers?: Readonly<Record<string, string>>;
}

/** Configurazione del client. */
export interface ApiClientOptions {
  /** URL base del gateway, senza barra finale. Vuoto = stessa origine. */
  baseUrl: string;
  /** Callback invocata quando una rotta protetta risponde 401. */
  onUnauthenticated?: () => void;
}

/** Client verso le API della piattaforma. */
export class ApiClient {
  private readonly baseUrl: string;
  private onUnauthenticated: (() => void) | undefined;

  /**
   * @param options Configurazione del client.
   */
  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.onUnauthenticated = options.onUnauthenticated;
  }

  /**
   * Registra (o rimuove) la reazione al 401 su una rotta protetta.
   *
   * Serve al provider di autenticazione, che nasce dopo il client e deve poter
   * riportare l'applicazione allo stato anonimo senza che il client conosca
   * React.
   *
   * @param handler Callback da invocare, oppure `undefined` per disattivarla.
   */
  setUnauthenticatedHandler(handler: (() => void) | undefined): void {
    this.onUnauthenticated = handler;
  }

  /**
   * Esegue una richiesta con la sessione a cookie.
   *
   * @param method Metodo HTTP.
   * @param path Percorso relativo al gateway, es. `/api/v1/projects`.
   * @param body Corpo JSON, se previsto.
   * @param options Opzioni della richiesta.
   * @returns Il corpo JSON della risposta, oppure `undefined` per 204.
   * @throws ApiError Se la risposta non è di successo.
   */
  async request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      [CSRF_HEADER]: CSRF_HEADER_VALUE,
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Il cookie di sessione è l'unica credenziale: senza questo il gateway
      // non vede alcuna sessione.
      credentials: 'include',
    });

    if (response.status === 401) {
      if (options.notifyUnauthenticated !== false) {
        this.onUnauthenticated?.();
        throw new ApiError(401, t('common.errors.sessionExpired'));
      }
      throw new ApiError(401, (await extractError(response)).message);
    }

    if (!response.ok) {
      const { message, code } = await extractError(response);
      throw new ApiError(response.status, message, parseRetryAfter(response), code);
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /**
   * Scarica un file (per esempio un CSV) con la sessione a cookie.
   *
   * Stesse regole di `request` (cookie, header CSRF, 401 che riporta
   * all'accesso, messaggio e codice letti dal corpo d'errore), ma il corpo
   * torna come `Blob` invece che come JSON. Il file lo salva poi
   * `saveBlob`: niente ticket in query string, perché la richiesta la fa il
   * client e non il browser da solo.
   *
   * @param path Percorso relativo al gateway, query compresa.
   * @returns Il contenuto.
   * @throws ApiError Se la risposta non è di successo.
   */
  async requestBlob(path: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE },
      credentials: 'include',
    });
    if (response.status === 401) {
      this.onUnauthenticated?.();
      throw new ApiError(401, t('common.errors.sessionExpired'));
    }
    if (!response.ok) {
      const { message, code } = await extractError(response);
      throw new ApiError(response.status, message, parseRetryAfter(response), code);
    }
    return response.blob();
  }

  /**
   * Carica un corpo binario con avanzamento, per esempio un archivio zip.
   *
   * `fetch` non riferisce l'avanzamento dell'invio, e per un archivio da
   * decine di megabyte l'utente deve vedere che il caricamento procede: per
   * questo qui si usa `XMLHttpRequest`, con le **stesse regole** di `request`
   * (cookie di sessione, header CSRF, 401 che riporta all'accesso, messaggio e
   * codice letti dal corpo d'errore).
   *
   * @param method Metodo HTTP, di solito `PUT`.
   * @param path Percorso relativo al gateway.
   * @param body Contenuto da inviare.
   * @param options Tipo del contenuto, avanzamento e interruzione.
   * @returns Il corpo JSON della risposta, `undefined` per 204.
   * @throws ApiError Se la risposta non è di successo o la rete cade.
   * @throws DOMException `AbortError` se il caricamento viene interrotto.
   */
  upload<T>(method: HttpMethod, path: string, body: Blob, options: UploadOptions): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (options.signal?.aborted === true) {
        reject(new DOMException('aborted', 'AbortError'));
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open(method, `${this.baseUrl}${path}`);
      // Stesse garanzie di `request`: cookie allegato e seconda difesa CSRF.
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader(CSRF_HEADER, CSRF_HEADER_VALUE);
      xhr.setRequestHeader('Content-Type', options.contentType);
      for (const [name, value] of Object.entries(options.headers ?? {})) {
        xhr.setRequestHeader(name, value);
      }

      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          options.onProgress?.(event.loaded, event.total);
        }
      };
      xhr.onerror = () => reject(new ApiError(0, t('common.errors.generic')));
      xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
      xhr.onload = () => {
        if (xhr.status === 401) {
          this.onUnauthenticated?.();
          reject(new ApiError(401, t('common.errors.sessionExpired')));
          return;
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          const { message, code } = parseErrorBody(xhr.responseText);
          reject(
            new ApiError(xhr.status, message, retryAfterOf(xhr.getResponseHeader('Retry-After')), code),
          );
          return;
        }
        if (xhr.status === 204 || xhr.responseText === '') {
          resolve(undefined as T);
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject(new ApiError(xhr.status, t('common.errors.generic')));
        }
      };

      options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(body);
    });
  }

  /**
   * Ottiene un ticket monouso per un URL che il browser costruirà da sé.
   *
   * Serve a `EventSource`, `<img src>` e `<a download>`, che non possono
   * impostare header. Il ticket vale per quella sola richiesta, dura pochi
   * secondi e si spende una volta: è il sostituto del token in query string.
   *
   * @param method Metodo per cui il ticket sarà valido.
   * @param path Percorso esatto per cui il ticket sarà valido.
   * @returns L'URL completo, già comprensivo del ticket.
   * @throws ApiError Se il gateway rifiuta di emettere il ticket.
   */
  async ticketedUrl(method: HttpMethod, path: string): Promise<string> {
    const { ticket } = await this.request<{ ticket: string }>('POST', '/api/v1/tickets', {
      method,
      path,
    });
    return `${this.baseUrl}${path}?ticket=${encodeURIComponent(ticket)}`;
  }
}

/**
 * Legge l'attesa suggerita dall'header `Retry-After`.
 *
 * La forma con data HTTP è ammessa dallo standard ma il gateway non la usa: un
 * valore non numerico viene trattato come «attesa non dichiarata», perché una
 * conversione approssimativa mostrerebbe all'utente un numero inventato.
 *
 * @param response Risposta da cui leggere l'header.
 * @returns I secondi di attesa, o `null` se non dichiarati.
 */
export function parseRetryAfter(response: Response): number | null {
  return retryAfterOf(response.headers.get('Retry-After'));
}

/**
 * Interpreta il valore dell'header `Retry-After`.
 *
 * @param raw Valore dell'header, `null` se assente.
 * @returns I secondi di attesa, o `null` se non dichiarati in secondi interi.
 */
export function retryAfterOf(raw: string | null): number | null {
  if (raw === null) {
    return null;
  }
  const seconds = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/** Messaggio e codice estratti dal corpo di una risposta di errore. */
export interface ErrorBody {
  /** Messaggio da mostrare all'utente. */
  message: string;
  /** Codice della regola di dominio violata, `null` se assente. */
  code: string | null;
}

/**
 * Interpreta il testo del corpo di una risposta di errore.
 *
 * I servizi rispondono `{"detail": "..."}`, e per le regole di dominio anche
 * `{"code": "..."}`. Un corpo diverso o illeggibile non deve produrre
 * un'eccezione secondaria che nasconde l'errore originale.
 *
 * @param text Corpo della risposta, come testo.
 * @returns Il messaggio da mostrare e il codice, se c'è.
 */
export function parseErrorBody(text: string): ErrorBody {
  try {
    const payload = JSON.parse(text) as { detail?: unknown; code?: unknown } | null;
    return {
      message:
        payload !== null && typeof payload.detail === 'string'
          ? payload.detail
          : t('common.errors.generic'),
      code: payload !== null && typeof payload.code === 'string' ? payload.code : null,
    };
  } catch {
    // Corpo non JSON: si ricade sul messaggio generico.
    return { message: t('common.errors.generic'), code: null };
  }
}

/**
 * Estrae messaggio e codice dal corpo di una risposta di errore.
 *
 * @param response Risposta non riuscita.
 * @returns Il messaggio da mostrare all'utente e il codice, se c'è.
 */
async function extractError(response: Response): Promise<ErrorBody> {
  try {
    return parseErrorBody(await response.text());
  } catch {
    return { message: t('common.errors.generic'), code: null };
  }
}
